import { Component, Input, OnInit, OnChanges, SimpleChanges, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { ModalComponent } from '../primeng/modal/modal.component';
import { FileApiService } from '@angular-workspace/services';
import { HttpClient } from '@angular/common/http';

export interface DataProviderEntityFormData {
  // User Details
  firstName?: string;
  lastName?: string;
  registeredEmail?: string;
  jobTitle?: string;
  employeeId?: string;
  
  // Entity Details
  entityType?: string;
  entitySector?: string;
  entityName?: string;
  websiteUrl?: string;
  address?: string;
  
  // Document file names
  documentFiles?: {
    incorporation?: string;
    panCard?: string;
    authorization?: string;
    others?: string;
  };
  
  // Document paths/keys for PDF viewing
  documentPaths?: {
    incorporation?: string;
    panCard?: string;
    authorization?: string;
    others?: string;
  };
}

@Component({
  selector: 'app-data-provider-entity-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    PdfViewerModule,
    ModalComponent,
  ],
  templateUrl: './data-provider-entity-form.component.html',
  styleUrl: './data-provider-entity-form.component.scss',
})
export class DataProviderEntityFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() formData: DataProviderEntityFormData = {};
  @Input() requestType?: string; // 'organisation_join' | 'organisation_create' | undefined

  dataProviderEntityForm!: FormGroup;
  
  // Computed property to check if it's a join request
  get isJoinRequest(): boolean {
    return this.requestType === 'organisation_join';
  }
  
  // Computed property to get full user name for join requests
  get fullUserName(): string {
    const firstName = this.formData.firstName || '';
    const lastName = this.formData.lastName || '';
    return `${firstName} ${lastName}`.trim() || '';
  }
  
  // Document file names (read-only display)
  documentFiles: { [key: string]: string } = {
    incorporation: '',
    panCard: '',
    authorization: '',
    others: '',
  };
  
  // Document paths/keys for PDF viewing
  documentPaths: { [key: string]: string } = {
    incorporation: '',
    panCard: '',
    authorization: '',
    others: '',
  };
  
  // PDF Preview Modal
  showPdfPreviewModal: boolean = false;
  currentPdfUrl: string | Uint8Array = '';
  currentPdfTitle: string = '';
  pdfLoading: boolean = false;
  documentLoadingStates: { [key: string]: boolean } = {
    incorporation: false,
    panCard: false,
    authorization: false,
    others: false,
  };
  // Cache blob URLs to avoid refetching
  private blobUrlCache: { [key: string]: string } = {};

  constructor(
    private fb: FormBuilder,
    private fileApiService: FileApiService,
    private http: HttpClient,
    private cdr: ChangeDetectorRef
  ) {}
  
  ngOnDestroy(): void {
    // Revoke all blob URLs to free memory
    Object.values(this.blobUrlCache).forEach(url => {
      if (url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    });
  }

  ngOnInit(): void {
    this.initForm();
    this.loadFormData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.dataProviderEntityForm && changes['formData']) {
      this.loadFormData();
    }
  }

  initForm(): void {
    // Single form with all fields disabled
    this.dataProviderEntityForm = this.fb.group({
      // User Details
      firstName: [{ value: '', disabled: true }],
      lastName: [{ value: '', disabled: true }],
      registeredEmail: [{ value: '', disabled: true }],
      jobTitle: [{ value: '', disabled: true }],
      employeeId: [{ value: '', disabled: true }],
      
      // Entity Details
      entityType: [{ value: '', disabled: true }],
      entitySector: [{ value: '', disabled: true }],
      entityName: [{ value: '', disabled: true }],
      websiteUrl: [{ value: '', disabled: true }],
      address: [{ value: '', disabled: true }],
      
      // Documents (read-only display)
      incorporation: [{ value: '', disabled: true }],
      panCard: [{ value: '', disabled: true }],
      authorization: [{ value: '', disabled: true }],
      others: [{ value: '', disabled: true }],
    });
  }

  loadFormData(): void {
    if (!this.formData) return;

    // Temporarily enable form controls to patch values
    Object.keys(this.dataProviderEntityForm.controls).forEach(key => {
      this.dataProviderEntityForm.get(key)?.enable();
    });

    // Map form data
    const formValues: any = {
      firstName: this.formData.firstName || '',
      lastName: this.formData.lastName || '',
      registeredEmail: this.formData.registeredEmail || '',
      jobTitle: this.formData.jobTitle || '',
      employeeId: this.formData.employeeId || '',
      entityType: this.formData.entityType || '',
      entitySector: this.formData.entitySector || '',
      entityName: this.formData.entityName || '',
      websiteUrl: this.formData.websiteUrl || '',
      address: this.formData.address || '',
    };

    // Patch form with data
    this.dataProviderEntityForm.patchValue(formValues);

    // Load document files
    if (this.formData.documentFiles) {
      this.documentFiles = {
        incorporation: this.trimFileName(this.formData.documentFiles.incorporation || ''),
        panCard: this.trimFileName(this.formData.documentFiles.panCard || ''),
        authorization: this.trimFileName(this.formData.documentFiles.authorization || ''),
        others: this.trimFileName(this.formData.documentFiles.others || ''),
      };
    }
    
    // Load document paths/keys
    if (this.formData.documentPaths) {
      this.documentPaths = {
        incorporation: this.formData.documentPaths.incorporation || '',
        panCard: this.formData.documentPaths.panCard || '',
        authorization: this.formData.documentPaths.authorization || '',
        others: this.formData.documentPaths.others || '',
      };
    }

    // Re-disable form controls
    Object.keys(this.dataProviderEntityForm.controls).forEach(key => {
      this.dataProviderEntityForm.get(key)?.disable();
    });
  }
  
  async openPdfPreview(documentType: string): Promise<void> {
    const documentPath = this.documentPaths[documentType];
    
    if (!documentPath) {
      console.error(`No path available for ${documentType}`);
      return;
    }
    
    // Check if we have a cached blob URL for this document
    if (this.blobUrlCache[documentType]) {
      // Use cached blob URL for faster loading
      this.currentPdfUrl = this.blobUrlCache[documentType];
      this.currentPdfTitle = this.getDocumentLabel(documentType);
      this.showPdfPreviewModal = true;
      return;
    }
    
    // Show modal immediately with loading state for better UX
    this.currentPdfTitle = this.getDocumentLabel(documentType);
    this.showPdfPreviewModal = true;
    this.documentLoadingStates[documentType] = true;
    this.pdfLoading = true;
    
    try {
      // Call downloadAssets with the payload
      const downloadPayload = {
        key: documentPath,
        expiresIn: 0
      };
      
      const downloadResponse = await this.fileApiService.downloadAssets(downloadPayload);
      console.log(`Download assets called successfully for ${documentType}:`, downloadResponse);
      
      // Extract the download URL from the response - check multiple possible response structures
      const downloadUrl = downloadResponse?.data?.url || 
                         downloadResponse?.data?.downloadUrl ||
                         downloadResponse?.url || 
                         downloadResponse?.downloadUrl ||
                         downloadResponse?.result?.url ||
                         downloadResponse?.result?.downloadUrl ||
                         downloadResponse?.result?.data?.url ||
                         downloadResponse?.result?.data?.downloadUrl;
      
      console.log(`Extracted download URL for ${documentType}:`, downloadUrl);
      
      if (downloadUrl) {
        // Try fetching as blob first to avoid X-Frame-Options issues
        // But do it more efficiently by showing the modal immediately
        try {
          const pdfBlob = await this.fetchPdfAsBlob(downloadUrl);
          const blobUrl = URL.createObjectURL(pdfBlob);
          
          // Cache the blob URL for this document type
          this.blobUrlCache[documentType] = blobUrl;
          
          // Set the PDF URL and ensure change detection runs
          this.currentPdfUrl = blobUrl;
          this.documentLoadingStates[documentType] = false;
          this.pdfLoading = false;
          
          // Trigger change detection to ensure PDF viewer renders
          this.cdr.detectChanges();
          
          console.log(`PDF blob URL created successfully for ${documentType}:`, blobUrl);
        } catch (fetchError: any) {
          console.warn(`Error fetching PDF as blob for ${documentType}, trying direct URL:`, fetchError);
          // Fallback: try using the URL directly (might not work due to X-Frame-Options)
          this.currentPdfUrl = downloadUrl;
          this.documentLoadingStates[documentType] = false;
          this.pdfLoading = false;
          
          // Trigger change detection to ensure PDF viewer renders
          this.cdr.detectChanges();
          
          console.log(`Using direct URL for ${documentType}:`, downloadUrl);
        }
      } else {
        console.error(`No download URL found in response for ${documentType}. Response:`, downloadResponse);
        this.currentPdfUrl = ''; // Clear URL to show error state
        this.documentLoadingStates[documentType] = false;
        this.pdfLoading = false;
      }
    } catch (downloadError: any) {
      console.error(`Error calling downloadAssets for ${documentType}:`, downloadError);
      this.documentLoadingStates[documentType] = false;
      this.pdfLoading = false;
    }
  }
  

  closePdfPreview(): void {
    this.showPdfPreviewModal = false;
    // Don't revoke the blob URL here - keep it cached for faster reopening
  }
  
  private async fetchPdfAsBlob(url: string): Promise<Blob> {
    try {
      // Use Fetch API directly to bypass Angular interceptors
      // This is necessary because signed S3 URLs are sensitive to headers
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/pdf,application/octet-stream,*/*'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      
      // Verify it's actually a PDF
      if (blob.type === 'application/pdf' || blob.type === 'application/octet-stream' || blob.size > 0) {
        return blob;
      } else {
        throw new Error('Response is not a valid PDF');
      }
    } catch (error) {
      console.error('Error fetching PDF:', error);
      throw error;
    }
  }

  getDocumentLabel(documentType: string): string {
    const labels: { [key: string]: string } = {
      incorporation: 'Firm Incorporation Certificate',
      panCard: 'PAN Card',
      authorization: 'Letter of Authorization',
      others: 'Other Documents',
    };
    return labels[documentType] || documentType;
  }

  /**
   * Trim UUID prefix from filename
   * Example: "1768325116304-03cefd0e-746d-47bb-83d1-f3f6d08803da-Forest-Stack Vocab.pdf"
   * Returns: "Forest-Stack Vocab.pdf"
   */
  trimFileName(fileName: string): string {
    if (!fileName) return '';
    
    // Check if filename contains UUID pattern (multiple dashes)
    const parts = fileName.split('-');
    
    // UUID pattern: timestamp (1 segment) + UUID (5 segments: 8-4-4-4-12 hex chars) = 6 segments total
    // If filename has 7+ segments, try to extract the actual filename part
    if (parts.length >= 7) {
      // Check if first segment is a timestamp (numeric) and segments 2-6 look like UUID parts
      const firstPart = parts[0];
      const isTimestamp = /^\d+$/.test(firstPart);
      const secondPart = parts[1];
      const isUuidPart1 = /^[0-9a-f]{8}$/i.test(secondPart); // 8 hex chars
      const thirdPart = parts[2];
      const isUuidPart2 = /^[0-9a-f]{4}$/i.test(thirdPart); // 4 hex chars
      const fourthPart = parts[3];
      const isUuidPart3 = /^[0-9a-f]{4}$/i.test(fourthPart); // 4 hex chars
      const fifthPart = parts[4];
      const isUuidPart4 = /^[0-9a-f]{4}$/i.test(fifthPart); // 4 hex chars
      const sixthPart = parts[5];
      const isUuidPart5 = /^[0-9a-f]{12}$/i.test(sixthPart); // 12 hex chars
      
      if (isTimestamp && isUuidPart1 && isUuidPart2 && isUuidPart3 && isUuidPart4 && isUuidPart5) {
        // Join everything from index 6 onwards (after timestamp + 5 UUID segments)
        return parts.slice(6).join('-');
      }
    }
    
    // If pattern doesn't match, return original filename
    return fileName;
  }
}
